import { useEffect, useRef, useState } from 'react';

/**
 * Поле за дата по нашему: ден, месец, година.
 *
 * Обикновеното `<input type="date">` се показва по езика на браузъра, не
 * на страницата — при английски Chrome излиза 07/10/2026 и се чете като
 * 7 октомври вместо 10 юли. Затова полето е текстово и показва
 * „10.07.2026“, а календарът се вика с копчето до него.
 *
 * Навън стойността винаги е ГГГГ-ММ-ДД — така я иска и сървърът.
 */
export default function DateField({ value, onChange, ...rest }) {
  const [text, setText] = useState(() => toBg(value));
  const picker = useRef(null);

  useEffect(() => { setText(toBg(value)); }, [value]);

  function commit(raw) {
    const iso = toIso(raw);
    // Празното поле е валиден избор — филтрите го ползват за „без граница“.
    if (raw.trim() === '') { onChange(''); return; }
    if (iso) { onChange(iso); setText(toBg(iso)); }
    else setText(toBg(value));   // неразбрано — връща се както е било
  }

  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      <input
        {...rest}
        inputMode="numeric"
        placeholder="дд.мм.гггг"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(e.currentTarget.value); }}
        style={{ width: 118, ...(rest.style ?? {}) }}
      />
      <input
        ref={picker}
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 0, height: 0, padding: 0, border: 0,
          opacity: 0, position: 'absolute', pointerEvents: 'none',
        }}
        tabIndex={-1}
      />
      <button
        type="button"
        title="Календар"
        onClick={() => picker.current?.showPicker?.() ?? picker.current?.focus()}
        style={{ padding: '6px 8px', lineHeight: 1 }}
      >📅</button>
    </span>
  );
}

/** ГГГГ-ММ-ДД -> ДД.ММ.ГГГГ */
function toBg(iso) {
  const s = String(iso ?? '').slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

/**
 * Приема каквото хората пишат: „18.8.26“, „18/08/2026“, „18-08-2026“.
 * Двуцифрената година е от този век — програмата не гледа назад към
 * 1926-а, а напред.
 */
function toIso(raw) {
  const parts = String(raw ?? '').trim().split(/[.\-/\s]+/).filter(Boolean);
  if (parts.length < 3) return null;

  const day = Number(parts[0]);
  const month = Number(parts[1]);
  let year = Number(parts[2]);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
  if (year < 100) year += 2000;
  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2000 || year > 2100) return null;

  const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  // Проверка срещу 31 февруари и подобни.
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime()) || d.getDate() !== day) return null;
  return iso;
}
